import {
  Alert,
  AlertDescription,
  Box,
  Button,
  Checkbox,
  CloseButton,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Icon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  MenuItem,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { FiX } from 'react-icons/fi';
import { FiFile } from 'react-icons/all';
import { AxiosRequestConfig } from 'axios';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';
import { Input } from '~/components';
import { queryClient } from '~/utils/react-query';
import apiSinister from '~/utils/apiSinister';

export const SinisterAddDocumentForm: React.FC<{
  sinisterId?: string | number;
}> = ({ sinisterId }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isFetching, setFetching] = useState(false);

  const [nomeDocumento, setNomeDocumento] = useState<string>('');
  const [nome, setNome] = useState<string>('');
  // const { data: types, isLoading: isTypeLoading } = useSinistersTypesAsSelect();
  // const { data: typesAux, isLoading: isTypeAuxLoading } = useSinistersTypes();

  const documentoRef = useRef<HTMLInputElement | null>(null);
  const [documento, setSelectedDocumento] = useState<any>();

  const { groups, headOffices, branches } = useContext(FilterContext);

  const {
    isOpen: isVisible,
    onClose: onCloseAlert,
    onOpen: onOpenAlert,
  } = useDisclosure({ defaultIsOpen: true });

  const handleOpen = () => {
    onOpen();
  };

  const handleClose = () => {
    clearValues();
    onClose();
  };
  const clearValues = () => {
    setNomeDocumento('');
    setNome('');
    setSelectedDocumento(null);
    onCloseAlert();
    clearErrors();
  };

  async function handleCreate() {
    clearErrors();

    !nome &&
      setError('nome', {
        type: 'required',
        message: 'Campo é obrigatório',
      });

    !(documento || nomeDocumento) &&
      setError('documento', {
        type: 'required',
        message: 'Arquivo é obrigatório',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = new FormData();
    form.append('nome', nome);
    if (documento) {
      form.append('arquivo', documento);
    }

    setFetching(true);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    const request = apiSinister.put(
      `anexar-documento/${sinisterId}`,
      form,
      config,
    );

    request
      .then(() => {
        toast({
          title: `Documento adicionado com sucesso`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        clearValues();
        onClose();
        queryClient.invalidateQueries('sinisterLog');
        queryClient.invalidateQueries('sinisterDocuments');
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 413) {
          toast({
            title: `Tamanho de requisição excedida`,
            description: 'Limite máximo para tamanho de arquivos é de 5MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          // erros de valid

          const errorsValidate = Object.entries(error.response.data);

          errorsValidate.forEach(err => {
            toast({
              title: `Erro ao adicionar documento`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao adicionar documento`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  function handleNome(e: any) {
    setNome(e.target.value);
    if (e.target.value.length <= 255) {
      clearErrors('nome');
    }
  }

  const handleDocumentoChange = (_file: File | null) => {
    setSelectedDocumento(_file);
    setNomeDocumento(_file ? _file.name : '');
    createSuggestion(_file?.name);
    clearErrors('nome');
    clearErrors('documento');
  };
  const clearDocumento = () => {
    setSelectedDocumento(null);
    setNomeDocumento('');
    setValue('documento', null);
  };

  function createSuggestion(fileName?: string) {
    const auxFileName = fileName
      ?.split('.')[0]
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .split(/-|_/);

    const finalName = auxFileName?.join(' ');

    setNome(finalName || '');
  }

  return (
    <>
      <Button
        color="white"
        background="gray.900"
        _hover={{
          bgColor: 'black',
        }}
        onClick={handleOpen}
      >
        Adicionar
      </Button>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="md"
        closeOnOverlayClick={!isFetching}
      >
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton isDisabled={isFetching} />
          <DrawerHeader>Adicionar Documento</DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" maxW={400} w="100%">
              <Stack gridGap={4}>
                {/* Documento */}
                <FormControl isInvalid={errors.documento}>
                  <FormLabel
                    htmlFor="writeUpFile"
                    style={{ fontSize: '14px' }}
                    justifyContent="space-between"
                    display="flex"
                  >
                    Documento *
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiFile} />
                    </InputLeftElement>
                    {(documento || nomeDocumento) && (
                      <InputRightElement pointerEvents="auto">
                        <Icon
                          as={FiX}
                          style={{
                            cursor: 'pointer',
                            pointerEvents: isFetching ? 'none' : 'auto',
                          }}
                          onClick={clearDocumento}
                        />
                      </InputRightElement>
                    )}
                    <input
                      id="file"
                      type="file"
                      onChange={e => {
                        handleDocumentoChange(
                          e.target.files ? e.target.files[0] : null,
                        );
                        e.target.value = '';
                      }}
                      accept=".pdf, image/png, image/gif, .jpg"
                      ref={documentoRef}
                      style={{ display: 'none' }}
                    />
                    <Input
                      placeholder="Selecione um arquivo"
                      onClick={() =>
                        documentoRef.current ? documentoRef.current.click() : ''
                      }
                      value={nomeDocumento || ''}
                      name="declaracao"
                      style={{ paddingLeft: '40px', paddingRight: '40px' }}
                      isReadOnly
                      error={errors.documento}
                      isDisabled={isFetching}
                    />
                  </InputGroup>
                </FormControl>
                <FormControl isInvalid={errors.nome}>
                  <Input
                    type="text"
                    label="Nome do documento *"
                    error={errors.nome}
                    fontSize="15px"
                    value={nome}
                    name="nome"
                    onChange={e => handleNome(e)}
                    isDisabled={isFetching}
                    placeholder="Nome do documento"
                  />
                </FormControl>
              </Stack>
            </Stack>
            <Button
              type="submit"
              onClick={handleSubmit(handleCreate)}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px' }}
              isDisabled={isFetching}
            >
              Cadastrar
              {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
            </Button>
            <Button
              type="submit"
              onClick={handleClose}
              color="black"
              background="transparent"
              style={{
                marginTop: '20px',
                marginLeft: '10px',
              }}
              isDisabled={isFetching}
            >
              Cancelar
            </Button>
            <Text
              style={{ fontSize: '12px', color: 'gray.500', marginTop: '20px' }}
            >
              * Campos obrigatórios
            </Text>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
